import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { api, apiError } from "../../lib/api";

export default function Profile() {
  const [form, setForm] = useState({ full_name: "", phone: "", dob: "", gender: "", allergies: "", medical_history: "" });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/patient/me").then((r) => {
      setForm({
        full_name: r.data.full_name || "",
        phone: r.data.phone || "",
        dob: r.data.dob || "",
        gender: r.data.gender || "",
        allergies: (r.data.allergies || []).join(", "),
        medical_history: r.data.medical_history || "",
      });
      setLoaded(true);
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/patient/me", {
        full_name: form.full_name,
        phone: form.phone,
        dob: form.dob || null,
        gender: form.gender || null,
        allergies: form.allergies ? form.allergies.split(",").map(s => s.trim()).filter(Boolean) : [],
        medical_history: form.medical_history || null,
      });
      toast.success("Profile saved");
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="patient-profile-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">◖ profile</div>
        <h1 className="mt-2 text-3xl tracking-tight font-light">Your profile</h1>
      </div>
      {loaded && (
        <form onSubmit={submit} className="rounded-xl border border-mb-border bg-mb-surface p-6 grid sm:grid-cols-2 gap-4">
          <Field label="Full name" testid="profile-fullname" value={form.full_name} required onChange={(v) => setForm({ ...form, full_name: v })} />
          <Field label="Phone" testid="profile-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
          <Field label="Date of birth" type="date" testid="profile-dob" value={form.dob} onChange={(v) => setForm({ ...form, dob: v })} />
          <Field label="Gender" testid="profile-gender" value={form.gender} onChange={(v) => setForm({ ...form, gender: v })} />
          <div className="sm:col-span-2">
            <Field label="Allergies (comma-separated)" testid="profile-allergies" value={form.allergies} onChange={(v) => setForm({ ...form, allergies: v })} />
          </div>
          <div className="sm:col-span-2 space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-mb-fg2">Medical history</Label>
            <Textarea value={form.medical_history} onChange={(e) => setForm({ ...form, medical_history: e.target.value })}
              rows={4} data-testid="profile-history"
              className="bg-mb-surface2 border-mb-border text-white rounded-lg focus-visible:ring-teal-500" />
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <Button type="submit" disabled={saving} data-testid="profile-save"
              className="bg-teal-500 hover:bg-teal-400 text-zinc-950 rounded-lg h-10 px-5 font-medium">
              <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required, testid }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-[0.2em] text-mb-fg2">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} required={required}
        data-testid={testid}
        className="bg-mb-surface2 border-mb-border text-white h-11 rounded-lg focus-visible:ring-teal-500" />
    </div>
  );
}
